/*
 * Scroll reveals.
 *
 * Three treatments, chosen from what the section actually is:
 *   - split sections  : media and copy arrive from their own sides
 *   - card grids      : children stagger in with a soft rise
 *   - everything else : a single soft rise
 *
 * Distances are deliberately short (28px / 14px). Long horizontal slides read
 * as a page-builder effect; short travel over a long duration reads considered.
 *
 * Performance:
 *  - IntersectionObserver only, so nothing runs on the main thread while scrolling
 *  - opacity + transform only, both compositor-driven
 *  - targets are unobserved once revealed, so the observer drains
 *  - above-the-fold content is never armed, so LCP is untouched
 *  - the hidden state is applied by JS, never the stylesheet, so a failed load
 *    leaves content visible rather than blank
 */
(function () {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  // Two-column sections whose halves should arrive from opposite sides.
  var SPLITS = [
    '.sn-story__grid',
    '.sn-compare__grid',
    '.sn-colour__grid',
    '.sn-momme__grid',
    '.sn-gifting__grid',
    '.sn-bundle__grid',
  ];

  // Grids whose children should stagger rather than move sideways.
  var GRIDS = [
    '.sn-collection__grid',
    '.sn-reviews__grid',
    '.sn-experts__grid',
    '.sn-proof__grid',
    '.sn-why__grid',
    '.sn-marks__list',
  ];

  // `transform` on a sticky element (or its ancestor) breaks sticky positioning,
  // so anything sticky is left alone entirely.
  function hasSticky(el) {
    if (getComputedStyle(el).position === 'sticky') return true;
    return !!el.querySelector('.sn-pdp__gallery, [style*="sticky"]');
  }

  function arm(el, x, delay) {
    if (hasSticky(el)) return null;
    el.classList.add('sn-rv');
    if (x) el.style.setProperty('--rv-x', x + 'px');
    if (delay) el.style.transitionDelay = delay + 'ms';
    return el;
  }

  function targetsFor(section) {
    var out = [];

    var split = section.querySelector(SPLITS.join(','));
    if (split && split.children.length >= 2) {
      var kids = Array.prototype.slice.call(split.children, 0, 2);
      // Each half travels from the side it already sits on.
      var first = arm(kids[0], -28, 0);
      var second = arm(kids[1], 28, 90);
      if (first) out.push(first);
      if (second) out.push(second);
      if (out.length) return out;
    }

    var grid = section.querySelector(GRIDS.join(','));
    if (grid && grid.children.length) {
      Array.prototype.forEach.call(grid.children, function (kid, i) {
        // Cap the stagger so long grids don't trail badly behind the scroll.
        var t = arm(kid, 0, Math.min(i, 7) * 70);
        if (t) out.push(t);
      });
      if (out.length) return out;
    }

    var self = arm(section, 0, 0);
    return self ? [self] : [];
  }

  function init() {
    var sections = Array.prototype.slice.call(document.querySelectorAll('.sn-section'));
    if (!sections.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('sn-rv--in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.04 }
    );

    sections.forEach(function (section) {
      // Anything already on screen renders normally — no fade on first paint.
      if (section.getBoundingClientRect().top < window.innerHeight * 0.9) return;
      targetsFor(section).forEach(function (t) {
        observer.observe(t);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
